import { useTranslation } from 'react-i18next';
import { SpreadButton } from '@anytag/shared-molecules';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { ColumnDef } from '@tanstack/react-table';
import { cn, formatNumber, getMkpReportLink } from '@anytag/shared-utils';
import { TableCell, SocialIcon, SocialIconProps } from '@anytag/shared-atoms';
import { useUserRoles, useReportPermissions, useAdvertiserPermissions, useMarketplacePermissions } from '@anytag/auth';
import {
  Marketplace,
  CampaignSocialMediaType,
  MarketplaceCampaignStatus,
  MarketplaceCampaignDetailType
} from '@anytag/graphql';
import { MarketplaceStatus } from '../../MarketplaceStatus';
import { SelectedMarketplaceCampaignType } from '../type';

interface Props {
  onDelete: (campaign: SelectedMarketplaceCampaignType) => void;
  onDuplicate: (campaign: SelectedMarketplaceCampaignType) => void;
}

export const useMarketplaceCampaignsTable = ({ onDelete, onDuplicate }: Props) => {
  const { t } = useTranslation();
  const { tc } = useTranslateCountry();
  const { hideReportLink } = useReportPermissions();
  const { hideEditAdvertiserBtn } = useAdvertiserPermissions();
  const { isAdminStaff, isAdminStaffAgency, isAdvertiser } = useUserRoles();
  const { hideEditMarketplaceBtn, hideDeleteMarketplaceBtn, hideMarketplaceReportLink } = useMarketplacePermissions();

  const hideCampaignLink = isAdvertiser ? hideMarketplaceReportLink : hideEditMarketplaceBtn;
  const columns: ColumnDef<Marketplace>[] = [
    {
      accessorKey: 'title',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Campaign Title'),
      cell: ({ row }) => {
        const campaignId = row.original.id.toString();
        const isECommerceCampaign = row.original.campaignType === MarketplaceCampaignDetailType.E_COMMERCE;
        const hasBadge = row.original.isSelectionCampaign || isECommerceCampaign;
        const hasReportLink =
          isAdvertiser &&
          !hideReportLink &&
          [
            MarketplaceCampaignStatus.ONGOING,
            MarketplaceCampaignStatus.FINISHED,
            MarketplaceCampaignStatus.SUSPENDED
          ].includes(row.original.status);
        const reportLink = getMkpReportLink({
          id: campaignId,
          mkpCampaignType: row.original.campaignType,
          isPostTracking: row.original.isPostTracking
        });

        return (
          <div className="campaign-title-column">
            <div className={cn({ 'pointer-events-none': hideCampaignLink })}>
              <TableCell.Link
                className="text-sm"
                label={row.original.title}
                params={{ id: campaignId }}
                to={hasReportLink ? reportLink.to : '/marketplace/$id'}
              />
              {hasBadge ? (
                <div className="mt-1 flex items-center gap-1">
                  {isECommerceCampaign ? (
                    <div style={{ ['--badge-color' as string]: '#40B87C' }} className={styles.badge}>
                      {t('Special Deal')}
                    </div>
                  ) : null}
                  {row.original.isSelectionCampaign ? (
                    <div style={{ ['--badge-color' as string]: '#FFB63D' }} className={styles.badge}>
                      {t('Selection')}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <SpreadButton
              collapseFrom={2}
              className="ml-1"
              options={[
                {
                  icon: 'post',
                  label: t('Posts'),
                  params: { id: campaignId },
                  to: '/marketplace/$id/posts',
                  tooltip: { help: t('Button.Posts') },
                  disabled: !row.original.isPostTracking
                },
                {
                  icon: 'chart',
                  label: t('Report'),
                  hidden: hideReportLink,
                  tooltip: { help: t('Button.Report') },
                  ...reportLink,
                  disabled: [MarketplaceCampaignStatus.REVIEWING, MarketplaceCampaignStatus.UPCOMING].includes(
                    row.original.status
                  )
                },
                {
                  icon: 'edit',
                  label: t('Edit'),
                  to: '/marketplace/$id',
                  params: { id: campaignId },
                  tooltip: { help: t('Button.Edit') },
                  hidden: hideEditMarketplaceBtn || hideDeleteMarketplaceBtn
                },
                {
                  icon: 'copy',
                  hidden: !isAdminStaff,
                  label: t('Duplicate'),
                  tooltip: { help: t('Button.Duplicate') },
                  onClick: () => onDuplicate({ id: row.original.id, title: row.original.title, type: 'duplicate' })
                },
                {
                  icon: 'delete',
                  label: t('Delete'),
                  hidden: hideDeleteMarketplaceBtn,
                  disabled: row.original.status !== MarketplaceCampaignStatus.REVIEWING,
                  onClick: () => onDelete({ id: row.original.id, title: row.original.title, type: 'delete' })
                }
              ]}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => <TableCell.Text value={tc(row.original.country.name)} className="min-w-25" />
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <MarketplaceStatus status={row.original.status} />
    },
    {
      accessorKey: 'advertiser',
      meta: { hidden: !isAdminStaffAgency },
      header: t<string>('HeaderColumn.Advertiser'),
      cell: ({ row }) => (
        <TableCell.Link
          to="/advertiser/$id"
          disabled={hideEditAdvertiserBtn}
          label={row.original.advertiser.name}
          params={{ id: row.original.advertiser.id.toString() }}
        />
      )
    },
    {
      accessorKey: 'dateRange',
      header: t<string>('HeaderColumn.Date Range'),
      cell: ({ row }) => (
        <TableCell.Period period={{ startDate: row.original.startDate, endDate: row.original.endDate }} />
      )
    },
    {
      accessorKey: 'socialMedia',
      header: t<string>('HeaderColumn.Social Media'),
      meta: { align: 'center' },
      cell: ({ row }) =>
        [CampaignSocialMediaType.UNSELECT, CampaignSocialMediaType.OTHER].includes(row.original.socialMedia) ? (
          <TableCell.Text value="-" className="min-w-0 text-center" />
        ) : (
          <div className="mx-auto block w-max">
            <SocialIcon type={row.original.socialMedia as SocialIconProps['type']} />
          </div>
        )
    },
    {
      accessorKey: 'unitCost',
      header: t<string>('HeaderColumn.Unit Cost'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Costs
          currency={t(row.original.currency)}
          socialMedia={row.original.socialMedia}
          costPerPost={row.original.costPerPost}
          costPerLike={row.original.costPerLike}
          costPerView={row.original.costPerView}
          costPerOrder={row.original.costPerOrder}
          costPerClick={row.original.costPerClick}
          costPerShare={row.original.costPerShare}
          costPerAction={row.original.costPerAction}
          costPerComment={row.original.costPerComment}
          commissionRate={row.original.commissionRate}
          costPerFollower={row.original.costPerFollower}
        />
      )
    },
    {
      accessorKey: 'budget',
      header: t<string>('HeaderColumn.Budget'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number
          unit={t(row.original.currency)}
          value={`${formatNumber(row.original.currentCost)} / ${formatNumber(row.original.budget)}`}
        />
      )
    },
    {
      accessorKey: 'salesPics',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Sales PIC'),
      cell: ({ row }) => (
        <TableCell.Text
          value={row.original.salesPics
            ?.slice(0, 2)
            ?.map((pic) => pic.name)
            ?.join(', ')}
        />
      )
    },
    {
      accessorKey: 'influencerManagementPics',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Influencer Management PIC'),
      cell: ({ row }) => (
        <TableCell.Text
          value={row.original.influencerManagementPics
            ?.slice(0, 2)
            ?.map((pic) => pic.name)
            ?.join(', ')}
        />
      )
    }
  ];

  return { columns };
};

const styles = {
  badge: cn(
    'inline-flex items-center gap-1 text-xxs font-normal text-tertiary',
    'before:h-2 before:w-2 before:rounded-full before:bg-[var(--badge-color)] before:content-[""]'
  )
};
